import { useEffect, useState } from "react";

export interface ComingSoonProps {
  /** The first phrase shown, e.g. "Coming Soon…". */
  eyebrow: string;
  /** The tagline whose words fade in, then all but `retainedWords` fade back out. */
  tagline: string;
  /**
   * The two words of `tagline` that survive and draw together into the `LoveHOA` wordmark. Matched
   * against `tagline`'s words with punctuation and case ignored, so "HOA…" in copy still matches
   * "HOA" here.
   */
  retainedWords: readonly [string, string];
}

type Phase = "eyebrow" | "tagline" | "final";

/** How long each of the eyebrow and tagline phases holds before advancing. */
const PHASE_DURATION_MS = 2800;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function bareWord(word: string): string {
  return word.replace(/[^\p{L}\p{N}]/gu, "").toLowerCase();
}

/**
 * The animated "coming soon" hero: `eyebrow` fades out, `tagline`'s words fade in, then every word
 * except `retainedWords` fades and collapses out of flow (opacity + max-inline-size + margin to
 * zero — a pure CSS flex-collapse, not a measured/FLIP animation), leaving the two retained words
 * sitting adjacent as the `LoveHOA` wordmark.
 *
 * The whole region carries one accessible name, `LoveHOA`, from the very first render — every
 * animating word is `aria-hidden`, so a screen reader never narrates the phases at all.
 * `prefers-reduced-motion: reduce` skips straight to the collapsed final state with no transitions.
 */
export function ComingSoon({ eyebrow, tagline, retainedWords }: ComingSoonProps) {
  const [phase, setPhase] = useState<Phase>(() => (prefersReducedMotion() ? "final" : "eyebrow"));

  useEffect(() => {
    if (phase === "eyebrow") {
      const id = setTimeout(() => setPhase("tagline"), PHASE_DURATION_MS);
      return () => clearTimeout(id);
    }
    if (phase === "tagline") {
      const id = setTimeout(() => setPhase("final"), PHASE_DURATION_MS);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [phase]);

  const words = tagline.split(/\s+/).filter(Boolean);

  return (
    <div role="img" aria-label="LoveHOA" className="site-coming-soon">
      {phase === "eyebrow" && (
        <p aria-hidden="true" className="site-coming-soon-eyebrow">
          {eyebrow}
        </p>
      )}
      {phase !== "eyebrow" && (
        <p aria-hidden="true" className="site-coming-soon-tagline">
          {words.map((word, index) => {
            const retained = retainedWords.some(
              (retainedWord) => bareWord(retainedWord) === bareWord(word),
            );
            const collapsed = phase === "final" && !retained;
            return (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: words are a fixed, ordered phrase, never reordered/filtered
                key={index}
                aria-hidden="true"
                className={`site-coming-soon-word site-coming-soon-word--${retained ? "retained" : "departing"}`}
                data-word-state={retained ? "retained" : "departing"}
                data-collapsed={collapsed ? "true" : undefined}
              >
                {word}
              </span>
            );
          })}
        </p>
      )}
    </div>
  );
}
